import React, { useEffect, useState } from 'react'
import { TrendingUpIcon, TrendingDownIcon, DollarSignIcon, BarChart3Icon, Loader2Icon, WrenchIcon } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useDivision } from '../contexts/DivisionContext'
import { matchesDivisionWithFallback } from '../lib/divisionFiltering'
import {
  MobilePage,
  MobilePageHeader,
  MobileListRow,
  MobileEmptyState,
  MobileStatStrip,
  MobileSection,
  mobile,
} from '../components/mobile'

interface CustomerBreakdown {
  customerId: string
  companyName: string
  revenue: number
  invoiceCount: number
}

export function PnLDashboard() {
  const { currentDivision } = useDivision()
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
  })

  const [totalRevenue, setTotalRevenue] = useState(0)
  const [totalCollected, setTotalCollected] = useState(0)
  const [laborCost, setLaborCost] = useState(0)
  const [materialsCost, setMaterialsCost] = useState(0)
  const [garbageBagsCost, setGarbageBagsCost] = useState(0)
  const [invoiceCount, setInvoiceCount] = useState(0)
  const [customerBreakdown, setCustomerBreakdown] = useState<CustomerBreakdown[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchPnLData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDivision])

  const fetchPnLData = async () => {
    try {
      setLoading(true)
      setError(null)

      const [invoicesRes, paymentsRes, timeEntriesRes, jobsRes] = await Promise.all([
        supabase
          .from('invoices')
          .select('id, total, customer_id, operational_division_id, customer:customers(company_name), contract:contracts(operational_division_id, service_type)')
          .gte('invoice_date', dateRange.start)
          .lte('invoice_date', dateRange.end),
        supabase
          .from('payments')
          .select('amount, invoice:invoices(operational_division_id, contract:contracts(operational_division_id, service_type))')
          .gte('payment_date', dateRange.start)
          .lte('payment_date', dateRange.end),
        supabase
          .from('time_entries')
          .select('total_hours, employee:employees(hourly_rate), job:jobs(operational_division_id, service_type)')
          .gte('clock_in_time', `${dateRange.start}T00:00:00`)
          .lte('clock_in_time', `${dateRange.end}T23:59:59`),
        supabase
          .from('jobs')
          .select('materials_cost, garbage_bags_cost, operational_division_id, service_type')
          .eq('status', 'completed')
          .gte('actual_end_time', `${dateRange.start}T00:00:00`)
          .lte('actual_end_time', `${dateRange.end}T23:59:59`),
      ])

      if (invoicesRes.error) throw invoicesRes.error
      if (paymentsRes.error) throw paymentsRes.error
      if (timeEntriesRes.error) throw timeEntriesRes.error
      if (jobsRes.error) throw jobsRes.error

      const invoices = (invoicesRes.data || []).filter((invoice: any) => {
        const contract = Array.isArray(invoice.contract) ? invoice.contract[0] || null : invoice.contract
        return matchesDivisionWithFallback(invoice, currentDivision, contract)
      })
      const payments = (paymentsRes.data || []).filter((payment: any) => {
        const invoice = Array.isArray(payment.invoice) ? payment.invoice[0] || null : payment.invoice
        const contract = Array.isArray(invoice?.contract) ? invoice.contract[0] || null : invoice?.contract
        return matchesDivisionWithFallback(invoice, currentDivision, contract)
      })
      const timeEntries = ((timeEntriesRes.data || []) as any[]).filter((entry) => {
        const job = Array.isArray(entry.job) ? entry.job[0] || null : entry.job
        return matchesDivisionWithFallback(job, currentDivision)
      })
      const jobs = (jobsRes.data || []).filter((job) =>
        matchesDivisionWithFallback(job, currentDivision)
      )

      const revenue = invoices.reduce((sum, inv) => sum + Number(inv.total || 0), 0)
      setTotalRevenue(revenue)
      setInvoiceCount(invoices.length)
      setTotalCollected(payments.reduce((sum, p) => sum + Number(p.amount || 0), 0))

      const labor = timeEntries.reduce((sum, entry) => {
        const rate = Number(entry.employee?.hourly_rate || 0)
        return sum + Number(entry.total_hours || 0) * rate
      }, 0)
      setLaborCost(labor)

      const materials = jobs.reduce((sum, job) => sum + Number(job.materials_cost || 0), 0)
      setMaterialsCost(materials)
      const bags = jobs.reduce((sum, job) => sum + Number(job.garbage_bags_cost || 0), 0)
      setGarbageBagsCost(bags)

      const byCustomer = new Map<string, CustomerBreakdown>()
      for (const inv of invoices) {
        const custId = inv.customer_id || 'unknown'
        const custName = (inv.customer as any)?.company_name || 'Unknown Customer'
        const existing = byCustomer.get(custId)
        const amount = Number(inv.total || 0)
        if (existing) {
          existing.revenue += amount
          existing.invoiceCount += 1
        } else {
          byCustomer.set(custId, {
            customerId: custId,
            companyName: custName,
            revenue: amount,
            invoiceCount: 1,
          })
        }
      }
      const breakdown = Array.from(byCustomer.values()).sort((a, b) => b.revenue - a.revenue)
      setCustomerBreakdown(breakdown)
    } catch (err: any) {
      console.error('Error fetching P&L data:', err)
      setError(err.message || 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  const totalCosts = laborCost + materialsCost + garbageBagsCost
  const grossProfit = totalRevenue - totalCosts
  const profitMargin = totalRevenue > 0 ? (grossProfit / totalRevenue) * 100 : 0

  const fmt = (n: number) =>
    `$${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`

  if (loading) {
    return (
      <MobilePage className="flex items-center justify-center min-h-[400px]">
        <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
      </MobilePage>
    )
  }

  return (
    <MobilePage>
      <MobilePageHeader
        title="P&L Dashboard"
        subtitle={<span className="hidden sm:inline">Profit and loss analysis and reporting</span>}
      />

      <MobileSection title="Report Period">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <label className={mobile.label}>Start Date</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              className={mobile.field}
            />
          </div>
          <div className="flex-1">
            <label className={mobile.label}>End Date</label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              className={mobile.field}
            />
          </div>
          <div className="flex items-end">
            <button type="button" onClick={fetchPnLData} className={`${mobile.primaryBtn} w-full sm:w-auto`}>
              Apply Filter
            </button>
          </div>
        </div>
      </MobileSection>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>
      )}

      <div className="lg:hidden">
        <MobileStatStrip
          stats={[
            { key: 'rev', label: 'Revenue', value: fmt(totalRevenue), tone: 'green' },
            { key: 'cost', label: 'Costs', value: fmt(totalCosts), tone: 'amber' },
            { key: 'profit', label: 'Gross profit', value: fmt(grossProfit), tone: grossProfit >= 0 ? 'blue' : 'red' },
            { key: 'margin', label: 'Margin', value: `${profitMargin.toFixed(1)}%`, tone: 'default' },
          ]}
        />
      </div>

      <div className="hidden lg:grid grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Revenue (Invoiced)</p>
              <p className="mt-2 text-2xl font-bold text-gray-900">{fmt(totalRevenue)}</p>
              <p className="mt-1 text-xs text-gray-500">
                {invoiceCount} invoices · {fmt(totalCollected)} collected
              </p>
            </div>
            <div className="p-3 rounded-lg bg-green-50 text-green-600">
              <DollarSignIcon className="w-6 h-6" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Costs</p>
              <p className="mt-2 text-2xl font-bold text-gray-900">{fmt(totalCosts)}</p>
              <p className="mt-1 text-xs text-gray-500">
                {fmt(laborCost)} labor · {fmt(materialsCost)} materials · {fmt(garbageBagsCost)} bags
              </p>
            </div>
            <div className="p-3 rounded-lg bg-orange-50 text-orange-600">
              <WrenchIcon className="w-6 h-6" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Gross Profit</p>
              <p className={`mt-2 text-2xl font-bold ${grossProfit >= 0 ? 'text-gray-900' : 'text-red-600'}`}>
                {fmt(grossProfit)}
              </p>
              <p className="mt-1 text-xs text-gray-500">revenue − costs</p>
            </div>
            <div className="p-3 rounded-lg bg-blue-50 text-blue-600">
              {grossProfit >= 0 ? <TrendingUpIcon className="w-6 h-6" /> : <TrendingDownIcon className="w-6 h-6" />}
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Profit Margin</p>
              <p className="mt-2 text-2xl font-bold text-gray-900">{profitMargin.toFixed(1)}%</p>
              <p className="mt-1 text-xs text-gray-500">{customerBreakdown.length} customers billed</p>
            </div>
            <div className="p-3 rounded-lg bg-purple-50 text-purple-600">
              <BarChart3Icon className="w-6 h-6" />
            </div>
          </div>
        </div>
      </div>

      <p className={mobile.sectionLabel}>Revenue by customer</p>
      <div className="native-mobile-list space-y-2">
        {customerBreakdown.length === 0 ? (
          <MobileEmptyState title="No invoiced revenue in this period." />
        ) : (
          customerBreakdown.map((c) => (
            <MobileListRow
              key={c.customerId}
              title={c.companyName}
              subtitle={`${c.invoiceCount} invoice${c.invoiceCount !== 1 ? 's' : ''}`}
              trailing={
                <span className="text-sm font-semibold text-gray-900 tabular-nums flex-shrink-0">
                  ${c.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              }
              meta={
                <div>
                  {totalRevenue > 0 ? ((c.revenue / totalRevenue) * 100).toFixed(1) : '0'}% of total · avg $
                  {(c.revenue / c.invoiceCount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              }
              chevron={false}
            />
          ))
        )}
      </div>

      <div className="native-desktop-table bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Revenue by Customer</h2>
        </div>
        {customerBreakdown.length === 0 ? (
          <div className="p-6 text-center text-gray-500">No invoiced revenue in this period.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoices</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Revenue</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">% of Total</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg / Invoice</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {customerBreakdown.map((c) => (
                  <tr key={c.customerId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {c.companyName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {c.invoiceCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${c.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-[100px]">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${totalRevenue > 0 ? (c.revenue / totalRevenue) * 100 : 0}%` }}
                          />
                        </div>
                        <span>{totalRevenue > 0 ? ((c.revenue / totalRevenue) * 100).toFixed(1) : '0'}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${(c.revenue / c.invoiceCount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </MobilePage>
  )
}
